// Pure helpers behind the Assistant (no DOM, easy to test).
import type { EmailItem, GlossaryEntry } from './api';

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface GlossaryMatcher {
  entry: GlossaryEntry;
  re: RegExp;
}

/** One whole-word, case-insensitive pattern per glossary entry (term + aliases; very short names skipped). */
export function glossaryMatchers(glossary: GlossaryEntry[]): GlossaryMatcher[] {
  const out: GlossaryMatcher[] = [];
  for (const entry of glossary) {
    const names = [entry.term, ...entry.aliases].map((n) => n.trim()).filter((n) => n.length >= 3);
    if (!names.length) continue;
    const alternatives = [...new Set(names)].sort((a, b) => b.length - a.length).map(escapeRe);
    out.push({ entry, re: new RegExp(`(?<![\\w-])(${alternatives.join('|')})(?![\\w-])`, 'i') });
  }
  return out;
}

/** Glossary entries mentioned in a stream of transcript segments, most recently mentioned first. */
export function spotTerms(segments: { at: number; text: string }[], matchers: GlossaryMatcher[], limit = 6): { entry: GlossaryEntry; at: number }[] {
  const seen = new Map<string, { entry: GlossaryEntry; at: number }>();
  for (const seg of segments) {
    for (const m of matchers) if (m.re.test(seg.text)) seen.set(m.entry.term, { entry: m.entry, at: seg.at });
  }
  return [...seen.values()].sort((a, b) => b.at - a.at).slice(0, limit);
}

const NUMBERS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
  twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, ninety: 90,
};

function wordsToNumber(words: string): number | null {
  if (/^\d+$/.test(words)) return Number(words);
  let parts = words.split(/[ -]+/);
  // "a ten minute timer": the article isn't a number unless it stands alone ("an hour").
  if (parts.length > 1 && (parts[0] === 'a' || parts[0] === 'an')) parts = parts.slice(1);
  let total = 0;
  for (const p of parts) {
    if (!(p in NUMBERS)) return null;
    total += NUMBERS[p];
  }
  return total || null;
}

export type KitchenCommand = { kind: 'timer'; seconds: number } | { kind: 'next' | 'back' | 'stop' };

/** Hands-free kitchen commands: "set a ten minute timer", "twenty five minutes", "next", "back", "stop". */
export function parseKitchenCommand(text: string): KitchenCommand | null {
  const t = text.toLowerCase().replace(/[^a-z0-9 -]/g, ' ');
  if (/\bhalf an hour\b/.test(t)) return { kind: 'timer', seconds: 1800 };
  const m = /\b(\d+|(?:(?:a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|ninety)[ -]?){1,2}) ?(hours?|minutes?|mins?|seconds?|secs?)\b/.exec(t);
  if (m) {
    const value = wordsToNumber(m[1].trim());
    if (value) return { kind: 'timer', seconds: value * (m[2].startsWith('h') ? 3600 : m[2].startsWith('s') ? 1 : 60) };
  }
  if (/\b(next|next step|done)\b/.test(t)) return { kind: 'next' };
  if (/\b(back|previous|go back)\b/.test(t)) return { kind: 'back' };
  if (/\b(stop|cancel|dismiss|quiet)\b/.test(t)) return { kind: 'stop' };
  return null;
}

/** Turn free-text notes into steps: one per line, bullets/numbers stripped. */
export function notesToSteps(notes: string | null | undefined): string[] {
  return (notes ?? '')
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*([-*•]|\d+[.)])\s*/, '').trim())
    .filter(Boolean);
}

/** Strip vault markup ($maths$, **bold**, `code`) for plain display. */
export function plainText(s: string): string {
  return s.replace(/\$+/g, '').replace(/\*\*|__|`/g, '').replace(/\s+/g, ' ').trim();
}

/** One pop-up per email *as flagged*: re-flagging it later counts as new. */
export const emailKey = (m: Pick<EmailItem, 'id' | 'triagedAt'>) => `${m.id}@${m.triagedAt ?? ''}`;

/**
 * Urgent emails to pop up during a task: priority 1, flagged after `since` (so a device opening the Assistant for
 * the first time doesn't replay old ones) and not dismissed yet. Most urgent first, as School Manager sorted them.
 */
export function emailPopups(items: EmailItem[], since: number, dismissed: ReadonlySet<string>): EmailItem[] {
  return items.filter((m) => {
    if (m.priority !== 1 || dismissed.has(emailKey(m))) return false;
    const flagged = m.triagedAt ? Date.parse(m.triagedAt) : NaN;
    return !Number.isNaN(flagged) && flagged > since;
  });
}
